import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";

const TRACKS = [
  {
    label: "Investment",
    title: "Investment Professionals",
    body:
      "Senior credit and equity professionals with documented underwriting experience across industrial credit, structured solutions and asset-backed equity. Multi-jurisdictional fluency preferred.",
  },
  {
    label: "Risk & Compliance",
    title: "Risk, Legal & Compliance",
    body:
      "Independent oversight professionals with cross-border regulatory experience (Cayman, US, UK, EU). AML/KYC programme delivery, conflicts management and operating-risk frameworks.",
  },
  {
    label: "Investor Relations",
    title: "Limited Partner Relations",
    body:
      "Senior IR professionals with institutional, family office and placement-agent network experience. Documented, considered correspondence is the operating standard.",
  },
  {
    label: "Operating Network",
    title: "Operating Partners (Network Roster)",
    body:
      "Senior industrial operators — not employees — engaged across diligence and value-creation on a documented advisory basis. Sectors: industrial manufacturing, logistics, energy transition, specialty chemicals.",
  },
];

const CareersPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.careersPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Careers"
          label="Selective Hiring · Partnership Track"
          title={
            <>
              A partnership built on{" "}
              <span className="italic text-[var(--kr-gold)]">selective</span>{" "}
              appointments.
            </>
          }
          description="Kellermann Reid & Co. does not maintain an open job board. The partnership grows through documented, considered appointments — initiated by introduction, evaluated through a confidential process, and concluded on a partnership track."
          breadcrumb="Careers"
        />

        {/* Posture */}
        <section className="py-20 lg:py-28">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Hiring Posture
                </span>
                <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.8vw,2.3rem)] leading-[1.15] max-w-[26ch]">
                We are not recruiting publicly.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pl-8">
              <p className="text-[var(--kr-text-muted)] text-[16px] leading-[1.95] max-w-[62ch] mb-6">
                The partnership is in strategic formation. Senior appointments
                are advanced on a confidential, documented basis — not through
                public listings or recruiter outreach. Where there is a fit, the
                process is direct, considered and respectful of professional
                continuity.
              </p>
              <p className="text-[var(--kr-text-muted)] text-[16px] leading-[1.95] max-w-[62ch]">
                If you are a senior professional whose track record aligns with
                one of the tracks below, the partnership welcomes a confidential
                introduction. Speculative applications outside these tracks are
                read but rarely advanced.
              </p>
            </div>
          </div>
        </section>

        {/* Tracks */}
        <section className="py-20 lg:py-28 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-12">
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Tracks
              </span>
              <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
              <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                Documented appointments only
              </span>
            </div>

            <div className="grid md:grid-cols-2 gap-0 border-t border-l border-[var(--kr-line)]">
              {TRACKS.map((t, i) => (
                <article
                  key={t.label}
                  className="p-10 border-r border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.03)] transition-colors duration-500"
                >
                  <div className="flex items-baseline justify-between mb-6">
                    <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                      {String(i + 1).padStart(2, "0")} · {t.label}
                    </span>
                  </div>
                  <h3 className="font-serif-display text-[1.55rem] text-[var(--kr-text)] mb-5 leading-[1.2] group-hover:text-[var(--kr-gold)] transition-colors">
                    {t.title}
                  </h3>
                  <p className="text-[var(--kr-text-muted)] text-[14.5px] leading-[1.8]">
                    {t.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-24 border-t border-[var(--kr-line)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <h3 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.6rem,2.6vw,2.2rem)] leading-[1.2] max-w-[36ch]">
                For confidential introductions, write directly to the{" "}
                <span className="italic text-[var(--kr-gold)]">partnership</span>
                .
              </h3>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-4 lg:justify-end">
              <Link
                to="/contact"
                data-testid={LANDING.careersInterest}
                className="kr-btn-primary"
              >
                Register Interest
                <span aria-hidden>→</span>
              </Link>
              <a
                href="mailto:investors@krprivatecapital.com"
                className="kr-btn-ghost"
              >
                investors@krprivatecapital.com
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default CareersPage;
